import React, { useState, useEffect } from 'react';
import { Box, Container, AppBar, Toolbar, Button } from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import Logo from '@components/Logo';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { LANDING_CONTAINER_MAX_WIDTH } from '@/constants/landingLayout';
import UnifiedPricingCalculator from '@components/UnifiedPricingCalculator';
import StatsSection from '@components/StatsSection';
import ClientLogosSection from '@components/ClientLogosSection';
import { apiService } from '@services/api';
import logger from '@utils/logger';
import CondoHeroSection from './CondoHeroSection';
import CondoFeaturesSection from './CondoFeaturesSection';
import CondoAIFeaturesSection from './CondoAIFeaturesSection';
import CondoTrustSection from './CondoTrustSection';
import CondoPricingSection from './CondoPricingSection';
import CondoFinalCtaSection from './CondoFinalCtaSection';

const CondominiumProductPage: React.FC = () => {
  const navigate = useNavigate();
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [defaultTrialDays, setDefaultTrialDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: body } = await apiService.getPublicTrialPeriodDefault();
        const days = body?.data?.defaultTrialPeriodDays;
        if (!cancelled && typeof days === 'number' && days > 0 && Number.isFinite(days)) {
          setDefaultTrialDays(Math.floor(days));
        }
      } catch (e) {
        logger.debug('Landing: usando fallback de dias de trial', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* AppBar com botão de Login */}
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Container maxWidth={LANDING_CONTAINER_MAX_WIDTH}>
          <Toolbar disableGutters sx={{ flexWrap: 'wrap', gap: 1, py: { xs: 1, sm: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Logo height={44} variant="logo" color="gray" sx={{ mr: 1 }} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: { xs: 1, sm: 2 },
                width: { xs: '100%', sm: 'auto' },
                ml: { xs: 0, sm: 'auto' },
              }}
            >
              <Button variant="outlined" onClick={() => navigate(`${ROUTES.LOGIN}?tab=signup`)}>
                Criar conta
              </Button>
              <Button variant="contained" startIcon={<LoginIcon />} onClick={() => navigate(ROUTES.LOGIN)}>
                Login
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <CondoHeroSection defaultTrialDays={defaultTrialDays} onNavigate={navigate} />

      <CondoFeaturesSection />

      <CondoAIFeaturesSection />

      {/* Stats Section */}
      <StatsSection />

      {/* Client Logos Section */}
      <ClientLogosSection />

      <CondoTrustSection />

      <CondoPricingSection onOpenCalculator={() => setCalculatorOpen(true)} />

      {/* Calculator Dialog */}
      <UnifiedPricingCalculator
        asDialog={true}
        open={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        allowMultipleClients={true}
      />

      <CondoFinalCtaSection defaultTrialDays={defaultTrialDays} onNavigate={navigate} />
    </Box>
  );
};

export default CondominiumProductPage;
